import { CSSProperties, ReactNode, useMemo, useState } from 'react';
import { Table as BaseTable, TableProps as BaseTableProps, ProgressBar } from '@ets/design-system';

import { useDispatch, useEffectOnUpdate, usePrevious, useSelector } from '_common/hooks';
import { setSelected } from '_common/components/Table/TableSlice';

import Footer from './Footer/Footer';

import styles from './Table.module.scss';

import type { SortingState, TableColumn, TableRow } from './Table';

export const LOADING_STATE = {
  INITIAL: 'INITIAL',
  ORDER: 'ORDER',
  FILTER: 'FILTER',
};

export type TableProps<T extends TableRow = TableRow> = {
  // Table identity in order to differenciate content from the other tables
  identity: Table.Identity;
  selectable?: boolean;
  renderEmptyState?: () => ReactNode;
  renderFooter?: () => ReactNode;
  lazyLoading?: {
    hasNextPage: boolean;
    offset: number;
    fetchNextPage: () => void;
    isLoading: boolean;
    totalFetched: number;
    totalLength: number;
  };
  order?: Request.OrderParams;
  onOrder?: (order: Request.OrderParams) => void;
  requestParams?: {
    params: Request.FilterParams & Request.OrderParams;
    setParams: (params: Request.FilterParams & Request.OrderParams) => void;
  };
  isLoading: boolean;
  loadingLabel: string;
  virtualized?: boolean;
  maxHeight?: CSSProperties['maxHeight'];
  selection?: Record<string, boolean>;
  id?: string;
} & Omit<BaseTableProps<T>, 'options'> & {
    options: Omit<BaseTableProps<T>['options'], 'columns'> & { columns: TableColumn<T>[] };
  };

const Table = <T extends TableRow = TableRow>({
  identity,
  selectable = true,
  lazyLoading,
  requestParams,
  renderEmptyState,
  renderFooter = () => <Footer />,
  isLoading,
  loadingLabel,
  virtualized = true,
  maxHeight,
  selection,
  id,
  ...props
}: TableProps<T>) => {
  const { options } = props;
  const { data, columns } = options;

  const dispatch = useDispatch();

  const selected = useSelector((state) => state.table.selected);

  const initialSorting = useMemo<SortingState>(() => {
    const order = props.order
      ? {
          order_field: props.order.order_field,
          order_type: props.order.order_type,
        }
      : requestParams
        ? {
            order_field: requestParams.params.order_field,
            order_type: requestParams.params.order_type,
          }
        : undefined;

    if (order) {
      return [{ id: order.order_field, desc: order.order_type === 'desc' }];
    }
    return [];
  }, [identity]);

  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const previousSorting = usePrevious(sorting);

  //#region Handle selection
  const handleSelectionChange: TableProps['onSelectionChange'] = (updater) => {
    const updatedState = typeof updater === 'function' ? updater(selected) : updater;
    dispatch(setSelected(updatedState));
  };

  //#endregion

  //#region Handle sorting
  useEffectOnUpdate(() => {
    /**
     * Only sort if the sorting has changed
     * Need this validation because an unecessary request would be made when "sorting" state is set
     */
    if (previousSorting && sorting) {
      if (
        previousSorting.length !== sorting?.length ||
        previousSorting[0]?.id !== sorting[0]?.id ||
        previousSorting[0]?.desc !== sorting[0]?.desc
      ) {
        const newOrder: Request.OrderParams = {
          order_field: sorting.length ? sorting[0].id : '_id',
          order_type: sorting.length ? (sorting[0].desc ? 'desc' : 'asc') : 'asc',
        };

        if (props.onOrder) {
          props.onOrder(newOrder);
        } else {
          if (requestParams) {
            requestParams.setParams({ ...requestParams.params, ...newOrder });
          }
        }
      }
    }
  }, [sorting]);
  //#endregion

  const tableMinWidth = useMemo(
    /**
     * The table has table-layout:fixed and the columns without a width defined (columns with a flex width)
     * can have a width of 0 when the table shrinks
     * To avoid this, we need to define a min-width for the table so it has enough space to render all the columns
     * The min-width will be the sum of all columns width or min-width, including prefix (32px) and suffix columns (32px)
     * This is assuming that the prefix and suffix columns have a width of 32px
     */
    () => columns.reduce((acc, column) => acc + (column.size ?? column.minWidth ?? 0), 0) + 32 + 32,
    [columns],
  );

  return (
    <div className={styles.root} id={id}>
      {isLoading && (lazyLoading?.offset === 0 || !lazyLoading) && (
        <div className={styles.loading}>
          <ProgressBar size="m" variant="primary-light" testId={`table-${identity}`}>
            {loadingLabel}
          </ProgressBar>
        </div>
      )}

      {(!data || data.length < 1) && !isLoading && renderEmptyState?.()}

      {!isLoading && data && data.length > 0 && (
        <div className={styles.content}>
          <div
            style={{
              overflow: 'auto',
              height: '100%',
              maxHeight,
            }}
          >
            <BaseTable
              {...props}
              sortingState={sorting}
              onSortingStateChange={setSorting}
              options={{
                manualSorting: !!props.order,
                manualPagination: true,
                ...options,
              }}
              getRowId={(row) => row.id}
              selectable={selectable}
              onSelectionChange={selectable ? handleSelectionChange : undefined}
              rowSelectionState={selection ? selection : selectable ? selected : undefined}
              virtualizeRows
              style={{ minWidth: tableMinWidth ?? 'auto' }}
              height={props.height ?? '100%'}
              infiniteScrollData={
                lazyLoading && import.meta.env.MODE !== 'test'
                  ? {
                      fetchNextPage: lazyLoading.fetchNextPage,
                      isFetching: lazyLoading.isLoading,
                      totalFetched: lazyLoading.totalFetched,
                      totalDBRowCount: lazyLoading.totalLength,
                      fetchMoreAt: 200,
                    }
                  : undefined
              }
            />
          </div>
        </div>
      )}
      {!isLoading && renderFooter()}
    </div>
  );
};

export default Table;
